import axios from 'axios';
import qs from 'qs';
import { logOutUser, updateTokens } from 'redux/actions/mobivers';
import { AppStore } from '../../../types/redux';
import { log } from '../../../utils/logger';
import { submitAsFormData } from '../../../utils/request';
import { getUrlDepandOnCustomization } from "../../../redux/actions/customizing";

/**
 * intercept any BFF communication to look for expired access tokens
 */
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		const expiredAccessToken =
			error.response &&
			error.response.data &&
			error.response.data.error_description &&
			error.response.data.error_description.includes('Access token expired');

		const unauthorized = (err: any) =>
			err.response && err.response.status === 401;

		log('intercepted response error', error);

		return new Promise((resolve, reject) => {
			const store = error.config.store;
			// Propagate API errors unrelated to outdated tokens
			if (!unauthorized(error)) {
				reject(error);
			} else {
				if (expiredAccessToken) {
					const { user } = store.getState();
					refreshUserToken(user.refresh_token, store)
						.then((data) => {
							log('refreshUserToken success', data, store);
							store.dispatch(
								updateTokens(data.access_token, data.refresh_token)
							);
							// Retry original request, rewriting the access token
							error.config.headers = apiConfig(error.config.store).headers;
							resolve(axios.request(error.config));
						})
						.catch((err) => {
							log('refreshUserToken error, logging out', err);
							store.dispatch(logOutUser());
							reject(error);
						});
				} else {
					log('expired session, logging out');
					store.dispatch(logOutUser());
					reject(error);
				}
			}
		});
	}
);

/**
 * Attempt to refresh the OAUTH access token
 * @param refreshToken the refresh token
 * @param store the redux store
 */
export const refreshUserToken = (
	refreshToken: string,
	store: AppStore
): Promise<any> => {
	const payload = qs.stringify({
		grant_type: 'refresh_token',
		refresh_token: refreshToken,
	});

	const config = {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		store,
		// NOTE: additional required headers set by the BFF depending on environment:
		// auth: { username: clientId, password: clientSecret }
	};

	return axios
		.post('/api/oauth/token', payload, config)
		.then((res) => res.data)
		.catch((error) => {
			if (
				error.response !== undefined &&
				error.response.data !== undefined &&
				error.response.data.error === 'invalid_grant'
			) {
				throw new Error('bad_credentials');
			}
			throw error;
		});
};

/**
 * Attempt a login via username / password and retrieve an OAUTH token
 * @param username the username
 * @param password the password
 */
export const fetchUserToken = (
	username: string,
	password: string
): Promise<any> => {
	const payload = qs.stringify({
		grant_type: 'password',
		username,
		password,
	});

	const config = {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		// NOTE: additional required headers set by the BFF depending on environment:
		// auth: { username: clientId, password: clientSecret }
	};

	return axios
		.post('/api/oauth/token', payload, config)
		.then((res) => res.data)
		.catch((error) => {
			log('fetchUserToken ERROR', error, error.response);
			if (
				error.response !== undefined &&
				error.response.data !== undefined &&
				error.response.data.error === 'invalid_grant'
			) {
				throw new Error('bad_credentials');
			}
			throw error;
		});
};

export const apiConfig = (store: AppStore): any => {
	const { user } = store.getState();

	return {
		headers: {
			'Content-Type': 'application/json',
			Authorization: 'Bearer ' + user.access_token,
		},
		store,
	};
};

/**
 * Redirect to the backoffice via POST request to perform a silent login
 * @param store the redux store
 */
export const requestBackofficeSSO = (store: AppStore): Promise<any> => {
	const { user, customizing } = store.getState();
	// these params need to be specified in the .env file
	const backofficeUrl = getUrlDepandOnCustomization(customizing, process.env.REACT_APP_BACKOFFICE_SSO_URL);
	const redirectUrl = process.env.REACT_APP_BACKOFFICE_REDIRECT_URL ?? '';

	if (!backofficeUrl) {
		return Promise.reject(
			'Backoffice Sign In failed because no backoffice SSO URL specified.'
		);
	}
	// store.dispatch(logOutUser());

	const data = {
		redirect_url: redirectUrl,
		token: user.access_token,
		refresh_token: user.refresh_token,
	};

	submitAsFormData(data, backofficeUrl, '_self', 'POST');

	return Promise.resolve();
};
